/* RESPONSIBLE TEAM: team-automation-foundations */
import type AppService from 'embercom/services/app-service';
import type Store from '@ember-data/store';
import Route from '@ember/routing/route';
import type Session from 'embercom/services/session';
import { inject as service } from '@ember/service';
import { hash } from 'rsvp';
import type TicketStateService from 'embercom/services/ticket-state-service';
import type WorkflowConnectorInsertableActionsService from 'embercom/services/workflow-connector-insertable-actions';
import type Transition from '@ember/routing/transition';
import type IntlService from 'embercom/services/intl';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';
import type AiContentSegmentsService from 'embercom/services/ai-content-segments-service';
import { ReactRoute } from 'embercom/lib/react-route';

export default class WorkflowsOverview extends ReactRoute(Route) {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare intercomEventService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare session: Session;
  @service declare ticketStateService: TicketStateService;
  @service declare workflowConnectorInsertableActions: WorkflowConnectorInsertableActionsService;
  @service declare aiAgentSetupService: AiAgentSetupService;
  @service declare aiContentSegmentsService: AiContentSegmentsService;

  titleToken = 'Workflows';

  analytics = {
    place: 'custom-bots-overview',
    section: 'operator',
  };

  reactEnabled(): boolean {
    // Check query parameter first using Ember's controller, if query param is true, check FF
    let controller = this.controllerFor('apps.app.automation.workflows-overview') as $TSFixMe;
    let reactQueryParam = controller.react;

    if (reactQueryParam === 'true' || reactQueryParam === '1') {
      return this.appService.app?.canUseFeature('workflows-overview-react');
    }

    return false;
  }

  get app() {
    return this.appService.app;
  }

  async beforeModel() {
    this.intercomEventService.trackEvent('visited-custom-bots', {
      onMatchingSystem: true,
    });

    this.session.switchWorkspace(this.app.id);
    this.preloadTicketCustomStates();
  }

  async model(_params: any, transition: Transition) {
    this.preloadEditorData();

    let finSetupState = await this.fetchFinSetupState();

    return hash({
      launchPaywall: transition.to.queryParams.paywall,
      finSetupState,
    });
  }

  private preloadEditorData() {
    this.store.findAll('custom-objects/object-type').catch(() => {});
    this.store.findAll('people/qualification-attribute').catch(() => {});
    this.store.findAll('messenger-app').catch(() => {});
    this.workflowConnectorInsertableActions.loadActions().catch(() => {});
    // Load AI content segments for Fin Audience filtering (if feature is enabled)
    if (this.app.canUseFeature('workflows-fin-audiences')) {
      this.aiContentSegmentsService.loadSegments().catch(() => {});
    }
    this.store.findAll('instagram/integration').catch(() => {
      this.notificationsService.notifyError(
        this.intl.t('workflows.channels.business-accounts.loading-error'),
      );
    });
  }

  private preloadTicketCustomStates() {
    Promise.all([
      this.session.workspace.fetchRequiredAttributes(),
      this.ticketStateService.setup(),
    ]).catch(() => {});
  }

  async fetchFinSetupState(): Promise<{ chatIsLive: boolean; emailIsLive: boolean }> {
    await this.aiAgentSetupService.loadSetupRulesets();

    return {
      chatIsLive: this.aiAgentSetupService.isFinSetupChatRulesetLive,
      emailIsLive: this.aiAgentSetupService.isFinSetupEmailRulesetLive,
    };
  }
}
